// ══════════════════════════════════════════════════════════════════════════════
// Aura attribution bridge — carrying "Aura sent me here" across a tab boundary
// ══════════════════════════════════════════════════════════════════════════════
// THE DEFECT THIS EXISTS TO FIX (found 2026-07-31 by asking why a conversion
// event that should be our most valuable number had never once fired):
//
//   click_type='enquire'  →  0 rows, ever, against 115 recorded clicks.
//
// The path was severed by design decisions that are each individually correct:
//
//   1. "View Profile" on a recommendation card opens the listing in a NEW TAB.
//   2. Aura's session id lives in sessionStorage, which is per-tab by definition
//      (auraCore's tab-isolation contract, deliberately chosen).
//   3. queryId lives only in the in-memory store and initialises to null.
//
// So the couple lands on the listing in a tab that has no idea Aura sent them.
// The enquiry form's guard — conversationDepth > 0 && queryId — is false, and
// the enquiry is recorded as though they arrived by search. Aura got the credit
// for none of the work it did. Every listing in the member report would read
// "0 enquiries" no matter how well Aura performed.
//
// The bridge is localStorage, which IS shared across tabs, holding the minimum
// needed to join the enquiry back to the conversation that caused it.
//
// PER-LISTING, NOT LAST-TOUCH. A couple discussing six venues with Aura and then
// enquiring with one must credit the conversation about THAT venue. A global
// "last Aura click" would hand the credit to whichever card they happened to
// open most recently, which is how attribution systems end up flattering
// whichever member was mentioned last.
//
// THIRTY DAYS. A wedding venue is not an impulse purchase: couples read, discuss,
// visit, and come back weeks later. A 24-hour window would under-credit Aura on
// precisely the considered decisions it is best at supporting. Thirty days is
// long enough to be fair and short enough that we are not claiming credit for a
// couple who forgot the conversation entirely.
//
// First-party, ids only — no names, no emails, nothing about the wedding.
// ══════════════════════════════════════════════════════════════════════════════

const KEY = 'lwd:aura:attribution';
const WINDOW_MS = 30 * 24 * 60 * 60 * 1000;
const MAX_ENTRIES = 60;

export interface AuraTouch {
  /** The Aura session that recommended this listing (the tab where the chat happened). */
  s: string;
  /** The specific query whose results contained it. */
  q: string;
  /** Rank at the moment of the click — cheap, and tells us whether position drives conversion. */
  r: number;
  /** Epoch ms. */
  t: number;
}

type TouchMap = Record<string, AuraTouch>;

function read(): TouchMap {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw) as TouchMap;
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch {
    return {};
  }
}

function write(map: TouchMap): void {
  try {
    localStorage.setItem(KEY, JSON.stringify(map));
  } catch {
    // Quota or a privacy mode that blocks storage. Attribution degrades to what it
    // was before this existed; nothing about the couple's experience changes.
  }
}

/** Prune expired entries, then cap by recency so the key cannot grow without bound. */
function prune(map: TouchMap): TouchMap {
  const cutoff = Date.now() - WINDOW_MS;
  const live = Object.entries(map).filter(([, v]) => v && typeof v.t === 'number' && v.t > cutoff);
  live.sort((a, b) => b[1].t - a[1].t);
  return Object.fromEntries(live.slice(0, MAX_ENTRIES));
}

/**
 * Record that Aura put this listing in front of the couple.
 *
 * Called from trackAuraClick, so it covers every existing click surface — card,
 * profile, shortlist, compare, map pin — without a new call site to forget. The
 * enquire event is deliberately excluded: it is the conversion being measured,
 * and letting it write a touch would let an enquiry attribute itself.
 */
export function recordAuraTouch(listingId: string, sessionId: string, queryId: string, rank: number): void {
  if (!listingId || !sessionId || !queryId) return;
  const map = read();
  map[listingId] = { s: sessionId, q: queryId, r: rank, t: Date.now() };
  // Pruned AFTER the insert, so the cap holds including the new entry. Pruning first
  // left MAX_ENTRIES + 1 in storage on every write.
  write(prune(map));
}

// ── enquiry_started: once per listing, per Aura session ──────────────────────
// A couple who opens the enquiry form, closes it to check a date, and opens it
// again is ONE couple who started an enquiry. Counting modal opens would make the
// funnel look healthier the more someone hesitated — the exact inversion of what
// hesitation means. Deduped here rather than in SQL because only the client can
// tell a reopen from a new open.
//
// Keyed by session AND listing: the same couple starting an enquiry with a second
// venue is a genuinely separate event, and a new conversation about the same venue
// weeks later is too.
const STARTED_KEY = 'lwd:aura:enquiry_started';

/**
 * Record that the enquiry form was opened for this listing. Returns true only the
 * FIRST time within this Aura session, so the caller can fire the funnel event
 * without having to remember whether it already did.
 */
export function markEnquiryStarted(listingId: string, sessionId: string): boolean {
  if (!listingId || !sessionId) return false;
  const key = `${sessionId}:${listingId}`;
  try {
    const raw = localStorage.getItem(STARTED_KEY);
    const map: Record<string, number> = raw ? JSON.parse(raw) : {};

    // Expire BEFORE the dedupe check. Checking first meant a mark never aged out, so a
    // couple returning months later to start a genuinely new enquiry was suppressed by
    // their own visit from the previous season.
    const cutoff = Date.now() - WINDOW_MS;
    const live = Object.entries(map)
      .filter(([, t]) => typeof t === 'number' && t > cutoff)
      .sort((a, b) => b[1] - a[1])
      .slice(0, MAX_ENTRIES * 2);      // two per listing is ample: one open, one re-open guard
    const next = Object.fromEntries(live);
    if (next[key]) return false;
    next[key] = Date.now();
    localStorage.setItem(STARTED_KEY, JSON.stringify(next));
    return true;
  } catch {
    // Storage unavailable: fire the event rather than lose it. A duplicate is a
    // smaller error than a silently missing funnel stage — the whole point of this
    // rung is that its absence is invisible.
    return true;
  }
}

/**
 * Did Aura send this couple to this listing, within the attribution window?
 *
 * Returns null when it did not — and the caller must treat null as "not from
 * Aura" rather than guessing. An enquiry we cannot honestly attribute is worth
 * less than an enquiry we wrongly claim, because the second kind is what makes a
 * member stop believing the numbers.
 */
export function getAuraTouch(listingId: string): AuraTouch | null {
  if (!listingId) return null;
  const touch = read()[listingId];
  if (!touch || typeof touch.t !== 'number') return null;
  return Date.now() - touch.t <= WINDOW_MS ? touch : null;
}
